<template>
  <EditionModal :is-displayed="id !== null" :query-param="pbReportingQueryParam" class="problem-reporting">
    <FiltersZone no-options-button>
      <Switcher slot="shortcuts" v-model="vueMode" :titles="vueModeTitles" />
    </FiltersZone>

    <div v-if="id !== null && problem !== null" class="f-h-below-filters">
      <!-- Simplified view -->
      <div v-if="vueMode === 0" class="problem-reporting__simplified problem-reporting__mode v-centerer">
        <div class="problem-reporting__simplified__column">
          <h2 class="basic-sub-title">
            {{ $t("message.VISUAL") }}
          </h2>
          <div class="problem-reporting__simplified__column__wrapper">
            <div class="legend legend--bottom-margin">{{ $t("message.ADD_PICTURE") }}</div>
            <FileChipList
              v-model="problem.files"
              editable
              column
              upload-zone
              pathname="problems/"
              own-file-viewer
              display-files
              class="problem-reporting__adv-form__synthesis__pictures"
              @input="fileViewerInput"
            />
          </div>
        </div>

        <div class="problem-reporting__simplified__column">
          <h2 class="basic-sub-title">{{ $t("message.DESCRIPTION") }}</h2>
          <div class="problem-reporting__simplified__column__wrapper">
            <div class="legend legend--bottom-margin" @click="$refs.description.doFocus()">
              {{ $t("message.DESCRIBE_PROBLEM") }}
            </div>
            <EditableLabel
              ref="description"
              v-model="problem.lWhat"
              class="problem-reporting__simplified__what"
              generic-border
              @input="fieldEdited('lWhat', problem.lWhat)"
              @click.native="$refs.description.doFocus()"
            />
            <div class="legend legend--bottom-margin">
              {{ $t("message.DEFINE_IMPACT") }}
            </div>
            <ImpactSelector
              v-if="problem && problem.impact !== undefined"
              v-model="problem.impact"
              @input="fieldEdited('impact', problem.impact)"
            />
          </div>
        </div>

        <div class="problem-reporting__simplified__column">
          <h2 class="basic-sub-title">{{ $t("message.LOCATION_AND_ACTIONS") }}</h2>
          <div class="problem-reporting__simplified__column__wrapper">
            <div class="legend legend--bottom-margin">{{ $t("message.LOCATE_PROBLEM") }}</div>
            <Tree
              ref="zonesTree"
              v-model="problem.zones"
              :items="allZones"
              multiple
              @input="fieldEdited('zones', problem.zones)"
            />
            <div class="problem-reporting__simplified__zone-names">
              <span>
                {{ decodeHTML(treeIdsText(allZones, problem.zones, 'name', 'zones')) }}
              </span>
              <br>
            </div>
            <div class="legend legend--bottom-margin">{{ $t("message.SECURING_ACTIONS") }}</div>
            <ActionItem
              v-for="(action, index) in securingActions"
              :key="action.id"
              :value="action"
              :all-users="allUsers"
              @fieldInput="editAction(action, $event)"
              @deleteClick="deleteAction(action, index)"
            />
            <button
              class="generic-border generic-border--text-btn generic-border--blue-border"
              @click="addAction"
            >
              <Icon>{{ addExtraBold }}</Icon>
              <span>{{ $t("message.ADD_SECURING_ACTION") }}</span>
            </button>
          </div>
        </div>
      </div>
      <!-- End of simplified view -->

      <!-- Advanced view -->
      <div v-else class="problem-reporting__adv-form problem-reporting__mode">

        <!-- Main full width field -->
        <div class="problem-reporting__adv-form__main-field">
          <Icon
            class="problem-reporting__adv-form__main-field__prev"
            :class="{ 'problem-reporting__adv-form__main-field__prev--disabled': advancedView === 0 }"
            @click.native="setAdvancedField(advancedView - 1)"
          >
            {{ arrowDownLine }}
          </Icon>
          <div class="problem-reporting__adv-form__main-field__wrapper">
            <div class="problem-reporting__adv-form__main-field__title">
              {{ $t(`message.${currentField.title}_TITLE`) }}
            </div>
            <div class="problem-reporting__adv-form__main-field__component">
              <component
                :is="currentField.component"
                v-if="currentField.component"
                :key="currentField.key"
                v-model="problem[currentField.key]"
                v-bind="currentFieldProps"
                :placeholder="$t(`message.${currentField.title}_TITLE`)"
                generic-border
                focus-on-mount
                @input="fieldEdited(currentField.key, problem[currentField.key])"
              />
              <div v-else-if="currentField.key === 'actions'">
                <ActionItem
                  v-for="(action, index) in securingActions"
                  :key="action.id"
                  :value="action"
                  :all-users="allUsers"
                  @fieldInput="editAction(action, $event)"
                  @deleteClick="deleteAction(action, index)"
                />
                <button
                  class="generic-border generic-border--text-btn generic-border--blue-border"
                  @click="addAction"
                >
                  <Icon>{{ addExtraBold }}</Icon>
                  <span>{{ $t("message.ADD_SECURING_ACTION") }}</span>
                </button>
              </div>

              <div class="problem-reporting__adv-form__main-field__help">
                {{ currentField.key === 'impact' ? impactHint : currentTeam.teamProblemHints[currentField.key] }}
              </div>
            </div>
          </div>
          <Icon
            class="problem-reporting__adv-form__main-field__next"
            @click.native="setAdvancedField(advancedView + 1)"
          >
            {{ arrowDownLine }}
          </Icon>
        </div>
        <!-- Synthesis header (summary of all the fields) -->
        <div class="problem-reporting__adv-form__synthesis">
          <!-- 5W1H, details, and other data columns -->
          <div
            v-for="(column, idx) in fieldsColumns"
            :key="idx"
            class="problem-reporting__adv-form__synthesis__column"
          >
            <div
              v-for="(field, fidx) in column"
              :key="field.key"
            >
              <div
                class="problem-reporting__adv-form__synthesis__item"
                :class="{
                  'problem-reporting__adv-form__synthesis__item--current': advancedView === idx * 7 + fidx
                }"
                @click="setAdvancedField(idx * 7 + fidx)"
              >
                <div class="legend legend--bottom-margin">
                  {{ $t(`message.${field.title}`) }}
                </div>
                <!-- eslint-disable vue/no-v-html -->
                <div
                  class="inner-child-inline notranslate"
                  v-html="field.readable
                    ? field.readable(problem[field.key])
                    : safeAutocompleteString(problem[field.key])"
                />
                <!-- eslint-enable vue/no-v-html -->
              </div>
              <div
                v-if="fidx !== column.length - 1 || $store.state.device.isSmartphoneLandscape"
                class="problem-reporting__adv-form__synthesis__sep"
              />
            </div>

            <div v-if="idx === 2">
              <div
                class="problem-reporting__adv-form__synthesis__item"
                :class="{ 'problem-reporting__adv-form__synthesis__item--current': advancedView === fields.length }"
                @click="setAdvancedField(fields.length - 1)"
              >
                <div class="legend legend--bottom-margin">
                  {{ $t('message.SECURING_ACTIONS') }}
                </div>
                <div class="problem-reporting__adv-form__synthesis__securing-actions-nb">
                  {{ problem && problem.actions ? problem.actions.length : 0 }}
                </div>
              </div>
              <div class="problem-reporting__adv-form__synthesis__sep" />
              <RecurrenceField
                ref="recField"
                class="problem-reporting__adv-form__synthesis__item"
                :problem="problem"
                :all-zones="allZones"
                :all-indicators="allIndicators"
                :all-keywords="allKeywords"
                :all-types="allTypes"
                :all-users="allUsers"
                @sheetEdit="refresh"
              />
              <div class="problem-reporting__adv-form__synthesis__sep" />
              <div class="problem-reporting__adv-form__synthesis__item">
                <div class="legend legend--bottom-margin">
                  {{ $t('message.FILES') }}
                </div>
                <FileChipList
                  v-model="problem.files"
                  editable
                  column
                  upload-zone
                  pathname="problems/"
                  own-file-viewer
                  display-files
                  class="problem-reporting__adv-form__synthesis__pictures"
                  @input="fileViewerInput"
                />
              </div>
            </div>
          </div>
        </div>
        <!-- End of synthesis -->
      </div>
      <!-- End of advanced view -->
    </div>

    <div class="problem-reporting__buttons">
      <button
        class="generic-border generic-border--text-btn generic-border--clickable generic-border--red"
        @click="confirmDeleteProblem"
      >
        <Icon>{{ deleteMedium }}</Icon>
        {{ $t("message.DELETE") }}
      </button>

      <div class="problem-reporting__buttons__right v-centerer">
        <button
          v-quick-title="$t('message.SAVE_BACK')"
          class="generic-border generic-border--text-btn generic-border--clickable generic-border--blue-border"
          @click="closeModal"
        >
          <Icon>{{ goBackMedium }}</Icon>
          {{ $t("message.SAVE") }}
        </button>
        <button
          v-quick-title="$t('message.SAVE_SHEET')"
          class="generic-border generic-border--text-btn generic-border--clickable generic-border--blue"
          @click="closeModal(true)"
        >
          <Icon>{{ pageMedium }}</Icon>
          {{ $t("message.SAVE") }}
        </button>
      </div>
    </div>
  </EditionModal>
</template>

<script>
/**
 * Component used to create a new problem in a modal.
 * Behaves as a router view but isn't.
 */

import toastr from 'toastr';
import { isEqual, cloneDeep } from 'lodash';
import { mapState } from 'vuex';
// Vuex
import {
  ADD_ACTION,
  ADD_PROBLEM,
  FETCH_SINGLE_PROBLEM,
  EDIT_PROBLEM,
  EDIT_ACTION,
  DELETE_ACTION,
} from '@/store/modules/problem/action-types';
import { LOG_CLB } from '@/store/modules/problem/mutation-types';
import {
  FETCH_ZONES,
  FETCH_USERS,
  FETCH_PB_TYPES,
  FETCH_INDICATORS_ONLY,
  FETCH_KEYWORDS,
} from '@/store/action-types';
import {
  GET_ZONES,
  GET_USERS,
  GET_PB_TYPES,
  GET_KEYWORDS,
} from '@/store/mutation-types';
// Import components
import ActionItem from '@/components/action-item.vue';
import DateField from '@/components/date-field.vue';
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import EditionModal from '@/components/edition-modals/edition-modal.vue';
import FileChipList from '@/components/file-system/file-chip-list.vue';
import FileViewer from '@/components/file-system/file-viewer.vue';
import FiltersZone from '@/components/filters-zone.vue';
import Icon from '@/components/icon.vue';
import ImpactSelector from '@/components/impact-selector.vue';
import RecurrenceField from '@/components/problem-sheet/recurrence-field.vue';
import Switcher from '@/components/switcher.vue';
import Tree from '@/components/tree.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import {
  calert,
  decodeHTML,
  treeAsArray,
  parseKeywords,
  treeIdsText,
  preventPastingStyles,
  safeAutocompleteString,
} from '@/utils/main';
import ACT from '@/utils/classes/AutocompleteChipsTypes';
import {
  addExtraBold,
  arrowDownLine,
  deleteMedium,
  goBackMedium,
  pageMedium,
} from '@/assets/icons';
import { ShizenGlobalEvent, globalEventDispatcher } from '@/utils/event-dispatcher/global';
import { pbReportingQueryParam, pbSheetQueryParam } from '@/utils/navigation-constants';
import { ROLE_ADMIN, ROLE_PROBLEMS } from '@/utils/constants';

export default {

  name: 'ProblemReportingModal',

  components: {
    ActionItem,
    DateField,
    EditableLabel,
    EditableLabelRadio,
    EditionModal,
    FileChipList,
    FileViewer,
    FiltersZone,
    Icon,
    ImpactSelector,
    RecurrenceField,
    Switcher,
    Tree,
  },

  mixins: [mixinSocketHandler],

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      deleteMedium,
      goBackMedium,
      pageMedium,
      pbReportingQueryParam,
      saving: false,
      // Problem reporting can be simplified (0) or advanced (1)
      vueModeTitles: [this.$t('message.SIMPLIFIED'), this.$t('message.ADVANCED')],
      problemDeleted: false,
      promptBeforeLeave: false,
      problem: null,
      // Advanced description fields
      fields: [
        { title: 'WHAT', key: 'lWhat', component: 'EditableLabel' },
        { title: 'WHO', key: 'lWho', component: 'EditableLabel' },
        { title: 'WHERE', key: 'lWhere', component: 'EditableLabel' },
        { title: 'WHEN', key: 'lWhen', component: 'EditableLabel' },
        { title: 'HOW', key: 'lHow', component: 'EditableLabel' },
        { title: 'HOW_MUCH', key: 'lHowMuch', component: 'EditableLabel' },
        { title: 'WHY', key: 'lWhy', component: 'EditableLabel' },
        {
          title: 'TYPE',
          key: 'types',
          component: 'Tree',
          readable: val => treeIdsText(this.allTypes, val, 'name', 'types'),
        },
        {
          title: 'LOCATION',
          key: 'zones',
          component: 'Tree',
          readable: val => treeIdsText(this.allZones, val, 'name', 'zones'),
        },
        {
          title: 'INDICATORS',
          key: 'indicators',
          component: 'EditableLabelRadio',
          readable: val => (val ? this.allIndicators.filter(i => val.includes(i.id)).map(i => i.name).join(', ') : ''),
        },
        { title: 'KEYWORDS', key: 'editableKeywords', component: 'EditableLabel' },
        { title: 'IMPACT', key: 'impact', component: 'ImpactSelector', readable: val => val + 1 },
        {
          title: 'DEADLINE',
          key: 'deadline',
          component: 'DateField',
          readable: val => (val ? this.$moment(val).format(this.$t('dateFormat')) : ''),
        },
        { title: 'SECURING_ACTIONS', key: 'actions', component: null },
      ],
      // Field number 0 being "Who", the last one being the deadline
      advancedView: 0,
      // Properties used by other components. See src/views/readme.md
      pageBackButtonAction: () => {
        this.quit(this.closeModal);
      },
      iaTeachText: this.$t('sensei.TEACHER_TEXT'),
      iaTeachBtns: [this.teacherBtn('SPOT_PB'), this.teacherBtn('W5H1')],
    };
  },

  computed: {
    /** Number: ID of the problem. If 0, creating a new problem */
    id() {
      return this.$route.query[pbReportingQueryParam] === undefined
        ? null
        : Number(this.$route.query[pbReportingQueryParam]);
    },
    /** Boolean: Whether the pb report was started because the user was following a meeting */
    followingMeeting() {
      return this.$route.query.followingMeeting && Number(this.$route.query.followingMeeting) === 1;
    },
    /** Object: GW data when reporting a problem from a GW */
    gwData() {
      if (this.$route.query.pbReportingGwData === undefined) {
        return null;
      }
      return JSON.parse(this.$route.query.pbReportingGwData);
    },
    /** Object: Current focused field in the advanced form */
    currentField() {
      return this.fields[this.advancedView];
    },
    /** Object; Custom props for the curren selected field in the advanced form */
    currentFieldProps() {
      switch (this.advancedView) {
        case 1:
          return { autocomplete: this.allUsers, autocompleteChipsType: ACT.USER };
        case 2:
          return {
            autocomplete: treeAsArray(this.allZones).map(z => ({ label: z.name, ...z })),
            autocompleteChipsType: ACT.ZONE,
          };
        case 7: return { items: this.allTypes, nameKey: 'name', childrenKey: 'types', multiple: true };
        case 8: return { items: this.allZones, multiple: true };
        case 9: return { items: this.allIndicators, multiple: true, labelKey: 'name' };
        case 10: return { autocomplete: this.allKeywords, autocompleteChipsType: ACT.KEYWORD, keywords: true };
        case 11: return { min: this.$store.state.device.isSmartphonePortrait };
        default: return {};
      }
    },
    /** Array: 3 columns to display a synthesis of the fields */
    fieldsColumns() {
      return [
        this.fields.slice(0, 7),
        this.fields.slice(7, 13),
        [],
      ];
    },
    /** String: Display the hint of the currently selected imapct */
    impactHint() {
      const hint = this.currentTeam.teamProblemHints.impact.find(h => h.impact === this.problem.impact);
      return hint ? hint.text : '';
    },
    /** Array: Securing actions of this problem */
    securingActions() {
      if (this.problem && this.problem.actions) {
        return this.problem.actions.filter(a => a.isSecuring === 1);
      }
      return [];
    },
    vueMode: {
      get() {
        const { pbReportingVueMode } = this.$route.query;
        if (pbReportingVueMode !== undefined) {
          return Number(pbReportingVueMode) === 1 ? 1 : 0;
        }
        return this.currentTeam.defaultReportingMode;
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        query.pbReportingVueMode = newVal === 1 ? 1 : 0;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Problem as it was last set in the state */
    stateProblem() {
      return this.$store.state.problem.problems[`${this.id}`];
    },
    // All required data
    ...mapState({
      allZones: state => state[GET_ZONES.stateKey] || [],
      allZonesLoading: state => state[GET_ZONES.loadingKey],
      allUsers: state => state[GET_USERS.stateKey] || [],
      allUsersLoading: state => state[GET_USERS.loadingKey],
      allTypes: state => state[GET_PB_TYPES.stateKey] || [],
      allTypesLoading: state => state[GET_PB_TYPES.loadingKey],
      allIndicators: state => (state.indicatorsArrayPerTeam[`${state.currentTeam.id}`] || {}).data || [],
      allIndicatorsLoading: state => (state.indicatorsArrayPerTeam[`${state.currentTeam.id}`] || {}).loading,
      allKeywords: state => state[GET_KEYWORDS.stateKey] || [],
      allKeywordsLoading: state => state[GET_KEYWORDS.loadingKey],
    }),
    // Properties used by other components. See src/views/readme.md
    pageTitle() {
      return this.promptBeforeLeave ? this.$t('message.NEW_PROBLEM') : this.$t('message.PROBLEM');
    },
  },

  watch: {
    id(newVal, oldVal) {
      if (this.id !== null) {
        // Id changes. The page must be refreshed and a new problem must be created if id === 0
        this.init();
        // If the old id was 0, this means that the action currently displayed is a new one
        this.promptBeforeLeave = oldVal === 0;
      } else {
        this.advancedView = 0;
        this.problem = null;
      }
    },
    stateProblem: {
      deep: true,
      handler(newVal) {
        if (newVal && !isEqual(newVal, this.problem)) {
          this.problem = cloneDeep(newVal);
        }
      },
    },
  },

  created() {
    if (this.id !== null) {
      this.init();
      this.promptBeforeLeave = this.id === 0;
    }

    this.handleSocketMessage(SocketMessages.CC_MSGS.PROBLEM_SHEET, (msg) => {
      if (Number(msg.id) !== Number(this.id)) return;
      this.refresh();
    });

    // NOTE: The purpose of this line is to identify the infinite problem creation loop issue.
    // eslint-disable-next-line max-len
    this.$store.commit(`problem/${LOG_CLB}`, `${this.uuid}: beforeRouteEnter - call init. followingMeeting = ${this.followingMeeting}`);

    // Fetch required data (zones, types, indicators, users, keywords)
    if (!this.allZonesLoading) {
      this.$store.dispatch(FETCH_ZONES);
    }
    if (!this.allUsersLoading) {
      this.$store.dispatch(FETCH_USERS);
    }
    if (!this.allTypesLoading) {
      this.$store.dispatch(FETCH_PB_TYPES);
    }
    if (!this.allIndicatorsLoading) {
      this.$store.dispatch(FETCH_INDICATORS_ONLY);
    }
    if (!this.allKeywordsLoading) {
      this.$store.dispatch(FETCH_KEYWORDS);
    }

    // Register the event from the router triggered when the user created a new action while another one is displayed.
    // If the modal is in the process of creating a new action,
    // the user will be prompted to keep/delete the new action or to cancel the navigation
    globalEventDispatcher.register(ShizenGlobalEvent.NEW_MODAL_WHILE_ONE_ACTIVE, (params) => {
      if (this.id !== null) {
        // Handle the event if the modal is currently displayed
        this.quit(params.next);
      }
    });
  },

  methods: {
    safeAutocompleteString,
    decodeHTML,
    treeIdsText,
    init() {
      if (!this.followingMeeting && Number(this.id) === 0) {
        const roleArray = [ROLE_ADMIN, ROLE_PROBLEMS];
        const hasRole = this.currentUser ? roleArray.find(r => this.currentUser.roles.includes(r)) > 0 : false;
        if ((this.currentUser && this.currentUser.restrictProblemCreationUpdate === 1 && !hasRole)
          || (this.currentTeam && this.currentTeam.restrictProblemCreationUpdate === 1)) {
          calert(this.$t('message.PB_NO_ACCESS'), [
              {
                title: this.$t('message.OK'),
                action: () => {
                  this.$router.back();
                },
              },
            ],
            null, null, null, () => { this.$router.back(); },
          );
        } else {
          this.createNewProblem();
        }
      } else {
        this.refresh();
        if (this.followingMeeting) {
          this.$router.replace({ query: { ...Object.assign({}, this.$route.query), followingMeeting: undefined } });
        }
      }
      // NOTE: The purpose of this line is to identify the infinite problem creation loop issue.
      this.$store.commit(`problem/${LOG_CLB}`, `${this.uuid}: init - problemLoaded=${!!this.id}`);
    },
    /** Create a new problem */
    createNewProblem() {
      const params = {};
      // If problem is added from a GW step, set the values of some fields
      let gwWhere = '';
      if (this.gwData !== null) {
        gwWhere = `${this.$t('message.GW')}${this.$t('message.TWO_DOTS')} ${this.gwData.gwName}`;
        if (this.gwData.stepName) {
          gwWhere += ` | ${this.$t('message.GW_STEP')}${this.$t('message.TWO_DOTS')} ${this.gwData.stepName}`;
        }
        if (this.gwData.clItemName) {
          gwWhere += ` | ${this.$t('message.GW_CHECKLIST_ITEM')}\
            ${this.$t('message.TWO_DOTS')} ${this.gwData.clItemName}`;
        }
        params.gwStep = this.gwData.stepId || 0;
        params.gwClItem = this.gwData.clItemId || 0;
        params.reportTeam = this.gwData.stepTeam || 0;
        params.lWhere = gwWhere;
        params.zones = [this.gwData.stepZone];
      }
      if (this.$route.query.pbIndicators) {
        params.indicators = this.$route.query.pbIndicators.split(',').map(id => Number(id));
      }
      this.$store.dispatch(`problem/${ADD_PROBLEM}`, params)
        .then((data) => {
          if (data && data.id) {
            const query = Object.assign({}, this.$route.query);
            query[pbReportingQueryParam] = data.id;
            this.$router.replace({ query });
            this.$nextTick(this.$refs.recField.getLinks);
          }
        })
        .catch((err) => {
          if (err && err.interrupted) {
            this.closeModal();
          }
        });
    },
    refresh() {
      // NOTE: The purpose of this line is to identify the infinite problem creation loop issue.
      this.$store.commit(`problem/${LOG_CLB}`, `${this.uuid}: refresh - problemId=${this.id}`);
      this.$store.dispatch(`problem/${FETCH_SINGLE_PROBLEM}`, { id: this.id });
    },
    /** Problem field was edited */
    fieldEdited(field, value, noMsgOnError = false) {
      const params = { id: this.id, noMsgOnError };
      if (field === 'editableKeywords') {
        params.keywords = parseKeywords(value);
      } else {
        params[field] = value;
      }
      if (this.gwData !== null) {
        params.stepId = this.gwData.stepId || 0;
        params.gwId = this.gwData.gwId || 0;
      }
      this.$store.dispatch(`problem/${EDIT_PROBLEM}`, params).then((data) => {
        if (typeof data === 'object') {
          toastr.remove();
          toastr.success(this.$t('message.PROBLEM_SAVED'));
          // Send socket to team and all teams that have access
          this.sendSocketUpdate();
        } else if (!noMsgOnError) {
          calert(this.$t('message.PB_EDITION_ERROR'), [
            { title: this.$t('message.OK') },
          ]);
        }
      });
    },
    /** Focus a new field using its index in this.fields */
    setAdvancedField(advView) {
      this.advancedView = advView.sBetweenBoundaries(0, this.fields.length - 1);
    },
    /** Files have been modified */
    fileViewerInput() {
      this.$store.dispatch(
        `problem/${EDIT_PROBLEM}`,
        { id: this.id, files: this.problem.files },
      ).then(this.sendSocketUpdate);
    },
    /** Add a securing action to the problem */
    addAction() {
      const params = { id: 0, problemId: this.id, isSecuring: true };
      this.$store.dispatch(`problem/${ADD_ACTION}`, params).then(({ data }) => {
        if (data instanceof Object && data.id && data.problem > 0) {
          this.$nextTick(preventPastingStyles);
          this.sendSocketUpdate();
        } else if (data === -2) {
          calert(this.$t('message.PB_NO_ACCESS'), [{ title: this.$t('message.OK') }]);
        } else {
          calert(this.$t('message.PB_EDITION_ERROR'), [{ title: this.$t('message.OK') }]);
        }
      });
    },
    /** Edit an action's field */
    editAction(action, field) {
      if (typeof action[field] !== 'undefined') {
        const params = { id: action.id };
        params[field] = action[field];
        this.$store.dispatch(`problem/${EDIT_ACTION}`, params).then(({ data }) => {
          toastr.remove();
          if (data instanceof Object) {
            toastr.success(this.$t('message.ACTION_UPDATED'));
            this.sendSocketUpdate();
            this.sendSocketMessage({
              team: action.affectTeam,
              id: action.id,
              msg: SocketMessages.CC_MSGS.ACTION_EDITION,
            });
            this.sendSocketMessage({ team: action.affectTeam, msg: SocketMessages.CC_MSGS.PENDING });
          } else {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
          }
        });
      }
    },
    /** Delete the action from server */
    confirmDeleteAction(action) {
      this.$store.dispatch(`problem/${DELETE_ACTION}`, { id: action.id }).then(({ data }) => {
        if (data) {
          toastr.success(this.$t('message.ACTION_DELETED'));
          this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.PENDING_AND_PROBLEMS, id: this.id });
          this.sendSocketUpdate();
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
        this.refresh();
      });
    },
    /** Ask the user to confirm action deletion */
    deleteAction(action, index) {
      calert(this.$t('message.CONFIRM_DELETE_ACTION'), [
        {
          title: this.$t('message.DELETE_ACTION'),
          action: () => { this.confirmDeleteAction(action, index); },
        },
        {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    sendSocketUpdate() {
      this.sendSocketMessage({ socketCompany: 1, id: this.id, msg: SocketMessages.CC_MSGS.PROBLEM_SHEET });
    },
    /** Close the problem reporting modal */
    closeModal(gotToSheet = false) {
      if (!this.problemDeleted) {
        globalEventDispatcher.dispatch(ShizenGlobalEvent.PROBLEM_REPORTED, { problem: this.problem });
      }
      if (gotToSheet === true) {
        const query = Object.assign({}, this.$route.query);
        delete query[pbReportingQueryParam];
        delete query.pbReportingVueMode;
        delete query[pbSheetQueryParam];
        this.$router.replace({ path: this.getPbSheetLink(this.id), query });
      } else {
        this.$router.back();
      }
    },
    /** Try to leave the page and prompt the user for confirmation if necessary */
    quit(next) {
      if (this.promptBeforeLeave) {
        calert(this.$t('message.PB_REPORT_SAVE_BEFORE_LEAVE'), [
          {
            title: this.$t('message.YES_LEAVE_SAVE'),
            action: () => {
              this.promptBeforeLeave = false;
              if (next) {
                this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.PROBLEMS, id: this.id });
                next();
              }
            },
            sClass: 'green',
          },
          {
            title: this.$t('message.NO_LEAVE_WITHOUT_SAVING'),
            action: () => {
              this.promptBeforeLeave = false;
              this.deleteProblem(next);
            },
            sClass: 'red',
          },
          {
            title: this.$t('message.CANCEL_NOT_LEAVING'),
          },
        ]);
      } else {
        this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.PROBLEMS, id: this.id });
        next();
      }
    },
    /** Delete button clicked, need confirmation */
    confirmDeleteProblem() {
      calert(this.$t('message.CONFIRM_DELETE_PB'), [
        {
          title: this.$t('message.DELETE'),
          action: () => {
            this.deleteProblem(this.closeModal);
          },
          sClass: 'red',
        },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    deleteProblem(next) {
      toastr.info(this.$t('message.PB_DELETING'));
      this.$http.post('/server/problems/delete.php', { id: this.id }).then(({ data }) => {
        this.problemDeleted = true;
        toastr.clear();
        if (data === 1) {
          if (next) {
            next();
          }
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.problem-reporting {

  .f-h-below-filters {
    height: calc(100% - #{$filters-zone-height} - #{$buttons-zone-height});
  }

  // Simplified form
  &__simplified {
    text-align: center;
    display: flex;
    align-items: stretch;

    .tree {
      overflow: auto;
      border: 1px solid $color-gray-background;
      max-height: 15rem;
      border-radius: $high-border-radius;
    }

    &__column {
      background: #fff;
      flex: 0 0 31.33%;
      height: 80%;
      margin: 0px 1%;
      vertical-align: top;
      min-width: 0px;
      border-radius: $main-border-radius;
      box-sizing: border-box;

      &__wrapper {
        height: calc(100% - calc(2 * #{$h2-margin}) - #{$h2-line-height});
        padding: 0px 1.5rem;
        overflow: auto;

        > :first-child {
          margin-top: 4rem;
        }

        > :last-child {
          margin-bottom: 4rem;
        }
      }
    }

    &__what.editable-label {
      margin-bottom: 2rem;
      text-align: initial;
    }

    &__zone-names {
      padding: 0px 10px;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      margin-bottom: 2rem;
    }

    .action-item {
      max-width: 100%;
      min-width: 100%;
    }
  }

  &__mode {
    height: 100%;
  }

  &__buttons {
    background: #fff;
    height: $buttons-zone-height;
    box-sizing: border-box;
    padding: $buttons-zone-padding;
    display: flex;
    align-items: center;
    justify-content: space-between;

    > * {
      font-weight: 600;
      justify-content: center;
    }

    &__right > * {
      margin-left: 1rem;
    }
  }

  // Advanced form
  &__adv-form {
    display: flex;
    flex-direction: column;
    padding-top: $page-content-padding;
    padding-bottom: $page-content-padding;
    box-sizing: border-box;

    &__main-field {
      flex: 1;
      display: flex;
      align-items: center;
      background: white;
      border-radius: $main-border-radius;
      min-height: 14rem;

      &__wrapper {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-flow: column;
        align-self: stretch;
        padding: 0.5rem 0;
      }

      &__component {
        min-width: 25rem;
        overflow: auto;
        margin: 0 5rem;
        padding: 0.3rem;

        .date-field {
          width: 100%;
        }
      }

      &__prev, &__next {
        flex: 0 0 auto;
        width: 1rem;
        height: 1rem;
        padding: 1.3rem;
        margin: 2rem;
        border-radius: 100px;
        cursor: pointer;

        &:hover {
          background: $color-gray-background-hover;
        }

        &:active {
          box-shadow: $color-shizen-blue-btn-shadow;
        }
      }

      &__prev {
        transform: rotate(90deg);
      }

      &__next {
        transform: rotate(-90deg);
      }

      &__title {
        text-align: center;
        font-size: 1.7rem;
        font-weight: 600;
        margin-bottom: 1.5rem;
      }

      &__help {
        white-space: pre-line;
        opacity: 0.6;
        font-size: 0.9rem;
        font-style: italic;
        line-height: 1.3rem;
        margin-top: 1rem;
      }
    }

    &__synthesis {
      flex: 0 0 auto;
      background: white;
      border-radius: $main-border-radius;
      margin-top: $page-content-padding;
      display: flex;
      overflow: auto;
      // current field has a min-height of 14rem and there's a bottom padding of 0.7rem
      max-height: calc(100% - 14.7rem);

      &__column {
        flex: 0 0 33.33%;
        padding: 0.5rem 0.8rem;
        box-sizing: border-box;
      }

      &__item {
        padding: 0.5rem;
        border-radius: $high-border-radius;
        cursor: pointer;

        &:hover {
          background: $main-hover-color;
        }

        &--current {
          background: $main-selection-color;
        }
      }

      &__sep {
        height: 1px;
        background: $color-gray-background;
        margin: 0.3rem 0;
      }

      .legend {
        font-size: 0.9rem;

        &--bottom-margin {
          margin-bottom: 0.7rem;
        }
      }

      &__securing-actions-nb {
        display: inline-flex;
        background: $color-shizen-gray-lighter;
        padding: 0.2rem 0.4rem;
        border-radius: $small-border-radius;
        font-size: 0.9rem;
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .problem-reporting {
    top: $smartphone-header-height;
    left: #{$smartphone-nav-width};
    height: calc(100% - #{$smartphone-header-height});
    width: calc(100% - #{$smartphone-nav-width});

    &__simplified {
      flex-flow: column;

      &.problem-reporting__mode {
        height: auto;
      }

      &__column {
        flex: 1;
        height: auto;
        width: 100%;
        margin-top: $page-content-padding;

        &:last-child {
          margin-bottom: $page-content-padding;
        }

        &__wrapper {
          height: auto;
        }
      }
    }

    &__adv-form {
      &__main-field {
        &__prev, &__next {
          margin: 1rem;
          padding: 1rem;
        }

        &__component {
          min-width: 18rem;
        }
      }

      &__synthesis {
        flex-flow: column;

        &__column {
          flex: 1;
          width: 100%;
          height: auto;

          &:not(:first-child) {
            padding-top: 0px;
          }

          &:not(:last-child) {
            padding-bottom: 0px;
          }
        }
      }
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .problem-reporting {
    &__adv-form {
      &__main-field {
        min-height: 20rem;

        &__title {
          font-size: 1.3rem;
        }
        &__prev, &__next {
          margin: .5rem;
        }

        &__component {
          min-width: 10rem;
          margin: 0 1rem;
        }
      }

      &__synthesis {
        max-height: calc(100% - 20.7rem);
      }
    }
  }
}
</style>
